import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { BaseTokenInterface } from '../../shared/interfaces/base-token-service.interface';
import { BaseOffersService } from '../../modules/offers/base-offers.service';
import { baseAuthSelectors, BaseCoreState, baseOffersActions } from '../../_store';
import { BaseAvailableFileTypeEnum } from '../../shared/enums/base-available-file-type.enum';

@Injectable()
export class BaseOffersEffects {
  loadOffersList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseOffersActions.loadOffersList),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        let payload = {
          ...action.payload,
          customer: user.organizationId,
        };
        return this.offersService.getOffersList(payload).pipe(
          mergeMap((response) => [
            baseOffersActions.setOffersList({ payload: response }),
            baseOffersActions.successOffersList(),
          ]),
          catchError(() => of(baseOffersActions.errorOffersList()))
        );
      })
    );
  });

  loadOfferDetails$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseOffersActions.loadOfferDetails),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        let payload = {
          offer: action.payload.offer,
          customer: user.organizationId,
        };
        return this.offersService.getOfferDetails(payload).pipe(
          mergeMap((response) => [
            baseOffersActions.setOfferDetails({ payload: response }),
            baseOffersActions.successOfferDetails(),
          ]),
          catchError(() => of(baseOffersActions.errorOfferDetails()))
        );
      })
    );
  });

  downloadOfferedDaysFile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseOffersActions.downloadOfferedDaysFile),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.offersService
          .getCustomerOfferedDaysFile({ customer: user.organizationId, offer: action.payload.offer })
          .pipe(
            tap((response) => {
              if (!action.payload.openInModal) {
                const blob = new Blob([response.body], { type: BaseAvailableFileTypeEnum.applicationPDF });
                const objectUrl = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.setAttribute('href', objectUrl);
                link.setAttribute('download', response.fileName);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }
            }),
            !action.payload.openInModal
              ? map(() => baseOffersActions.successDownloadOfferedDaysFile())
              : mergeMap((response) => [
                  baseOffersActions.setDownloadOfferedDaysFile({
                    payload: {
                      document: new Blob([response], { type: BaseAvailableFileTypeEnum.applicationPDF }),
                    },
                  }),
                  baseOffersActions.successDownloadOfferedDaysFile(),
                ]),
            catchError(() => of(baseOffersActions.errorDownloadOfferedDaysFile()))
          );
      })
    );
  });

  constructor(
    private actions$: Actions,
    private store: Store<BaseCoreState>,
    private offersService: BaseOffersService
  ) {}
}
